import React, { useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  Button,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  AccountCircle,
  Contacts as ContactsIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Group as GroupIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PhoneBook, { PhoneBookRef } from './PhoneBook';
import PhoneManagement from './PhoneManagement';
import UserManagement from './UserManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const MainApp: React.FC = () => {
  const { user, logout } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const phoneBookRef = useRef<PhoneBookRef>(null);

  // Menü-Handler
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const isAdmin = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return false;
    return (user.profile.groups as string[]).includes('telefonbuch_admin');
  };

  const getUserRoleLabel = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return 'Betrachter';
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) return 'Administrator';
    if (groups.includes('telefonbuch_user')) return 'Benutzer';
    if (groups.includes('telefonbuch_viewer')) return 'Betrachter';
    return 'Betrachter';
  };

  const getUserRoleColor = (): 'primary' | 'secondary' | 'error' => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return 'primary';
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) return 'error'; // Rot für Admin
    if (groups.includes('telefonbuch_user')) return 'secondary'; // Lila für User
    if (groups.includes('telefonbuch_viewer')) return 'primary'; // Blau für Viewer
    return 'primary';
  };

  const getUserRoleIcon = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return null;
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) {
      return <AdminIcon color="error" />;  // Rot für Admin
    } else if (groups.includes('telefonbuch_user')) {
      return <EditIcon color="secondary" />;  // Lila für User (Edit-Icon)
    } else if (groups.includes('telefonbuch_viewer')) {
      return <ViewIcon color="primary" />;  // Blau für Viewer
    }
    return null;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <PhoneIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Telefonbuch-System
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getUserRoleIcon()}
            <Chip
              label={getUserRoleLabel()}
              color={getUserRoleColor()}
              size="small"
            />
            
            <Button
              color="inherit"
              startIcon={<AccountCircle />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleUserMenuClick}
              sx={{ textTransform: 'none' }}
            >
              {user?.profile.name || user?.profile.preferred_username || 'Benutzer'}
            </Button>

            {/* Benutzer Dropdown Menü */}
            <Menu
              anchorEl={userMenuAnchorEl}
              open={Boolean(userMenuAnchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { minWidth: 280 }
              }}
            >
              {/* Benutzerinformationen */}
              <MenuItem disabled>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {user?.profile.name || user?.profile.preferred_username || 'Benutzer'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.profile.email || 'Keine E-Mail'}
                  </Typography>
                </Box>
              </MenuItem>
              
              <Divider />

              {/* Admin-spezifische Menüoptionen */}
              {isAdmin() && (
                <>
                  {/* Admin-Funktionen */}
                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.openImportDialog(); }}>
                    <CloudUploadIcon sx={{ mr: 2 }} />
                    <Typography>Agrarmonitor Import</Typography>
                  </MenuItem>

                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.openAgrarmonitorSetup(); }}>
                    <BuildIcon sx={{ mr: 2 }} />
                    <Typography>Agrarmonitor-Setup</Typography>
                  </MenuItem>

                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.downloadXML(); }}>
                    <DownloadIcon sx={{ mr: 2 }} />
                    <Typography>XML Export</Typography>
                  </MenuItem>

                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.checkPhoneNumbers(); }}>
                    <CheckCircleIcon sx={{ mr: 2 }} />
                    <Typography>
                      {phoneBookRef.current?.isCheckingPhoneNumbers() ? 'Prüfe Nummern...' : `Telefonnummern prüfen (${phoneBookRef.current?.getContactsCount() || 0})`}
                    </Typography>
                  </MenuItem>

                  <Divider />

                  {/* Gefährliche Aktionen */}
                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.deleteAgrarmonitorContacts(); }}>
                    <DeleteSweepIcon sx={{ mr: 2 }} color="warning" />
                    <Typography color="warning.main">
                      Agrarmonitor-Kontakte löschen ({phoneBookRef.current?.getAgrarmonitorContactsCount() || 0})
                    </Typography>
                  </MenuItem>

                  <MenuItem onClick={() => { handleUserMenuClose(); phoneBookRef.current?.deleteAllContacts(); }}>
                    <DeleteSweepIcon sx={{ mr: 2 }} color="error" />
                    <Typography color="error.main">
                      Alle Kontakte löschen ({phoneBookRef.current?.getContactsCount() || 0})
                    </Typography>
                  </MenuItem>

                  <Divider />
                </>
              )}

              {/* Logout */}
              <MenuItem onClick={() => { handleUserMenuClose(); handleLogout(); }}>
                <LogoutIcon sx={{ mr: 2 }} />
                Abmelden
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
        
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.dark' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="secondary"
            variant="fullWidth"
          >
            <Tab 
              icon={<ContactsIcon />} 
              label="Kontakte" 
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            {isAdmin() && (
              <Tab 
                icon={<PhoneIcon />} 
                label="Telefon-Verwaltung" 
                id="tab-1"
                aria-controls="tabpanel-1"
              />
            )}
            {isAdmin() && (
              <Tab 
                icon={<GroupIcon />} 
                label="Benutzer-Verwaltung" 
                id="tab-2"
                aria-controls="tabpanel-2"
              />
            )}
          </Tabs>
        </Box>
      </AppBar>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <PhoneBook ref={phoneBookRef} />
      </TabPanel>
      
      {isAdmin() && (
        <TabPanel value={tabValue} index={1}>
          <PhoneManagement />
        </TabPanel>
      )}
      
      {isAdmin() && (
        <TabPanel value={tabValue} index={2}>
          <UserManagement />
        </TabPanel>
      )}
    </Box>
  );
};

export default MainApp;
